import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Check, Smartphone, Zap } from "lucide-react";
import PixCheckoutModal from "../components/PixCheckoutModal";

const CARD_META = {
  m1: {
    testid: "plan-card-m1", name: "Para testar",
    features: ["1 chip simultâneo", "Ideal para testar a plataforma", "Loop de aquecimento 24/7 na nuvem", "Entrada automática em grupos"],
  },
  m5: {
    testid: "plan-card-m5", name: "Afiliados iniciantes",
    features: ["Até 5 chips simultâneos", "Tudo do MaturaMax 1", "Conversas cruzadas anti-ban", "Delays humanizados configuráveis"],
  },
  m10: {
    testid: "plan-card-m10", name: "Plano Estrela", popular: true,
    features: ["Até 10 chips simultâneos", "Melhor custo-benefício", "Chamadas de voz e vídeo simuladas", "Suporte prioritário"],
  },
  m20: {
    testid: "plan-card-m20", name: "Expansão de nichos",
    features: ["Até 20 chips simultâneos", "Para expandir a esteira de nichos", "Tudo do MaturaMax 10", "Grupos controlados"],
  },
  m50: {
    testid: "plan-card-m50", name: "Plano Tubarão",
    features: ["Até 50 chips simultâneos", "Estrutura de contingência profissional", "Tudo do MaturaMax 20", "Gerente de conta dedicado"],
  },
};

export default function Planos() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const locked = location.state?.locked;
  const [plans, setPlans] = useState([]);
  const [checkoutPlan, setCheckoutPlan] = useState(null);

  useEffect(() => {
    // Carregar planos
    api.get("/payments/plans").then(({ data }) => setPlans(Array.isArray(data) ? data : data?.plans ?? [])).catch(() => {});

    // Instrumentação do Funil: plan_page_viewed com trava anti-F5 de 10 minutos no sessionStorage
    try {
      const lastViewed = sessionStorage.getItem("mmax_plan_viewed_ts");
      const now = Date.now();
      if (!lastViewed || now - parseInt(lastViewed, 10) > 10 * 60 * 1000) {
        api.post("/funnel/track", { event_name: "plan_page_viewed" }).catch(() => {});
        sessionStorage.setItem("mmax_plan_viewed_ts", now.toString());
      }
    } catch (e) {
      // Fallback gracioso
    }
  }, []);

  const plansArr = Array.isArray(plans) ? plans : [];
  const displayPlans = ["m1", "m5", "m10", "m20", "m50"].map((key) => {
    const meta = CARD_META[key];
    const p = plansArr.find((x) => x.key === key);
    return {
      key,
      ...meta,
      label: p?.label || `MaturaMax ${key.replace("m", "")}`,
      price: p?.price ?? null,
      chips: p?.chip_limit ?? 0,
    };
  });

  const subscribe = (plan) => {
    // Instrumentação do Funil: plan_selected no momento exato do clique
    try {
      api.post("/funnel/track", {
        event_name: "plan_selected",
        plan_id: plan.key,
        plan_name: plan.label,
        amount: plan.price ?? 0,
      }).catch(() => {});
    } catch (e) {
      // Ignora erro silenciosamente para não travar a experiência do usuário
    }

    setCheckoutPlan({ key: plan.key, label: plan.label, price: plan.price ?? 0 });
  };

  return (
    <div className="space-y-8" data-testid="planos-page">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Planos e Assinatura</h1>
        <p className="text-sm sm:text-base text-slate-400 mt-2 leading-relaxed">
          Escolha quantos chips você precisa aquecer ao mesmo tempo. Pague via Pix ou cartão no checkout seguro da InfinitePay com liberação automática após a confirmação.
        </p>
      </div>

      {locked && (
        <div className="max-w-2xl mx-auto bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200 text-center" data-testid="planos-locked-banner">
          Sua assinatura está inativa. Escolha um plano abaixo e conclua o pagamento para liberar seus chips na nuvem.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-stretch">
        {displayPlans.map((plan, i) => {
          const isCurrent = user?.plan === plan.key;
          return (
            <div key={plan.key} className="relative group">
              <div className={`absolute -inset-0.5 rounded-2xl blur-lg transition-opacity duration-300 ${
                plan.popular
                  ? "bg-gradient-to-r from-emerald-500/40 to-cyan-500/40 opacity-100"
                  : "bg-gradient-to-r from-emerald-500/15 to-cyan-500/15 opacity-50 group-hover:opacity-90"
              }`} />
            <div
              data-testid={plan.testid}
              className={`relative bg-[#161F32] rounded-xl p-6 flex flex-col h-full transition-all duration-200 hover:-translate-y-1 animate-fade-up ${
                plan.popular
                  ? "border border-emerald-500/50"
                  : "border border-slate-800 hover:border-slate-700"
              }`}
              style={{ animationDelay: `${i * 100}ms` }}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-extrabold px-3.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-emerald-950 uppercase tracking-widest shadow-[0_0_18px_rgba(16,185,129,0.55)] flex items-center gap-1 whitespace-nowrap">
                  <Zap className="w-3 h-3" /> Mais Popular
                </span>
              )}
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{plan.name}</p>
              <h2 className="font-heading text-xl font-bold text-slate-100 mt-1">{plan.label}</h2>
              <div className="mt-4 mb-1">
                <span className="text-4xl font-extrabold tracking-tight text-emerald-400 font-mono">
                  {plan.price != null ? `R$ ${plan.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—"}
                </span>
                <span className="text-sm text-slate-500">/mês</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300 mb-5">
                <Smartphone className="w-4 h-4 text-emerald-400" />
                {plan.chips === 1 ? "1 chip conectado" : `${plan.chips} chips conectados simultaneamente`}
              </div>
              <ul className="space-y-2.5 flex-1 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-400">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <Button disabled variant="outline" data-testid={`btn-subscribe-plan-${plan.key}`}
                  className="w-full border-emerald-500/40 text-emerald-300">
                  Plano atual
                </Button>
              ) : (
                <Button
                  onClick={() => subscribe(plan)}
                  data-testid={`btn-subscribe-plan-${plan.key}`}
                  className={`w-full font-semibold transition-colors duration-150 ${
                    plan.popular
                      ? "bg-emerald-500 hover:bg-emerald-600 text-emerald-950"
                      : "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"
                  }`}
                >
                  Assinar agora
                </Button>
              )}
            </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-500">
        Você será redirecionado ao checkout da InfinitePay (Pix e cartão). Seu plano é liberado automaticamente após a confirmação do pagamento.
      </p>

      <PixCheckoutModal plan={checkoutPlan} onClose={() => setCheckoutPlan(null)} onPaid={() => refresh()} />
    </div>
  );
}
